// src/components/admin/sections/AdminTeam.tsx - VERSIÓN FINAL SIN ALERTS JS
import React, { useState, useEffect } from 'react';
import apiElCristo, { TeamMember, TeamData } from '../../../service/api_elcristo';
import './AdminTeam.css';

const AdminTeam: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{ member: TeamMember, index: number } | null>(null);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [teamData, setTeamData] = useState<TeamData>({
    subtitle: '',
    title: '',
    teamMembers: []
  });

  const [formData, setFormData] = useState<TeamMember>({
    name: '',
    role: '',
    image: '',
    teamShape: '',
    socialLinks: {
      facebook: '',
      twitter: '',
      youtube: '',
      linkedin: ''
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getTeamData();
      console.log('📥 Team data cargada:', data);
      setTeamData(data);
    } catch (error) {
      console.error('Error loading team:', error);
      setMessage({ type: 'error', text: 'Error al cargar el equipo' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'subtitle' | 'title') => {
    const value = e.target.value;
    setTeamData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '❌ Solo se permiten PNG, JPG o WEBP' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ La imagen debe ser menor a 3MB' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setUploadingImage(true);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'team');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Error al subir imagen');
      }
      
      const data = await response.json();
      
      console.log('✅ Imagen subida:', data.url);
      
      setFormData(prev => ({ ...prev, image: data.url }));
      
    } catch (err: any) {
      console.error('❌ Error upload:', err);
      setMessage({ type: 'error', text: `❌ ${err.message || 'Error al subir foto'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOpenModal = (member?: TeamMember, index?: number) => {
    setValidationError(null);
    
    if (member && index !== undefined) {
      setEditingMember(member);
      setEditingIndex(index);
      
      setFormData({
        name: member.name,
        role: member.role,
        image: member.image,
        teamShape: member.teamShape || '',
        socialLinks: member.socialLinks || {
          facebook: '',
          twitter: '',
          youtube: '',
          linkedin: ''
        }
      });
    } else {
      setEditingMember(null);
      setEditingIndex(null);
      setFormData({
        name: '',
        role: '',
        image: '',
        teamShape: '',
        socialLinks: {
          facebook: '',
          twitter: '',
          youtube: '',
          linkedin: ''
        }
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setEditingIndex(null);
    setValidationError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('social.')) {
      const socialField = name.split('.')[1] as keyof typeof formData.socialLinks;
      setFormData(prev => ({
        ...prev,
        socialLinks: { ...prev.socialLinks, [socialField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveMember = async () => {
    if (!formData.name || !formData.role || !formData.image) {
      setValidationError('Por favor completa todos los campos requeridos (Nombre, Cargo y Fotografía)');
      return;
    }

    const cleanSocialLinks = {
      facebook: formData.socialLinks?.facebook || '',
      twitter: formData.socialLinks?.twitter || '',
      youtube: formData.socialLinks?.youtube || '',
      linkedin: formData.socialLinks?.linkedin || ''
    };

    const memberToSave: TeamMember = {
      name: formData.name,
      role: formData.role,
      image: formData.image,
      teamShape: formData.teamShape || '',
      socialLinks: cleanSocialLinks
    };

    let updatedMembers: TeamMember[];
    if (editingIndex !== null) {
      updatedMembers = [...teamData.teamMembers];
      updatedMembers[editingIndex] = memberToSave;
    } else {
      updatedMembers = [...teamData.teamMembers, memberToSave];
    }

    const updatedTeamData: TeamData = {
      subtitle: teamData.subtitle,
      title: teamData.title,
      teamMembers: updatedMembers
    };

    console.log('💾 Guardando al backend:', updatedTeamData);

    try {
      setSaving(true);
      setValidationError(null);
      
      await apiElCristo.updateSiteContent({ team: updatedTeamData });
      
      setTeamData(updatedTeamData);
      setMessage({ 
        type: 'success', 
        text: editingIndex !== null ? '✅ Miembro actualizado correctamente' : '✅ Miembro agregado correctamente'
      });
      setTimeout(() => setMessage(null), 3000);
      
      handleCloseModal();
      
    } catch (error: any) {
      console.error('❌ Error guardando:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar el miembro'}` });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteModal = (member: TeamMember, index: number) => {
    setMemberToDelete({ member, index });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    const updatedMembers = teamData.teamMembers.filter((_, i) => i !== memberToDelete.index);
    const updatedTeamData: TeamData = {
      subtitle: teamData.subtitle,
      title: teamData.title,
      teamMembers: updatedMembers
    };

    try {
      setSaving(true);
      await apiElCristo.updateSiteContent({ team: updatedTeamData });
      
      setTeamData(updatedTeamData);
      setMessage({ type: 'success', text: '✅ Miembro eliminado correctamente' });
      setTimeout(() => setMessage(null), 3000);
      
      handleCloseDeleteModal();
      
    } catch (error: any) {
      console.error('❌ Error eliminando:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al eliminar'}` });
    } finally {
      setSaving(false);
    }
  };

  const handleMoveMember = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= teamData.teamMembers.length) return;

    const members = [...teamData.teamMembers];
    [members[index], members[newIndex]] = [members[newIndex], members[index]];
    
    const updatedTeamData: TeamData = {
      subtitle: teamData.subtitle,
      title: teamData.title,
      teamMembers: members
    };

    try {
      setSaving(true);
      await apiElCristo.updateSiteContent({ team: updatedTeamData });
      
      setTeamData(updatedTeamData);
      setMessage({ type: 'success', text: '✅ Orden actualizado correctamente' });
      setTimeout(() => setMessage(null), 2000);
      
    } catch (error: any) {
      console.error('❌ Error reordenando:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al reordenar'}` });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTitles = async () => {
    try {
      setSaving(true);
      await apiElCristo.updateSiteContent({ team: teamData });
      
      setMessage({ type: 'success', text: '✅ Títulos actualizados correctamente' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error guardando títulos:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar títulos'}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-team-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando Equipo Docente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-team-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-users"></i> Equipo Docente</h1>
          <p>Administrar profesores y personal del colegio</p>
        </div>
        <button className="btn btn-primary" onClick={() => handleOpenModal()} disabled={saving}>
          <i className="fas fa-user-plus"></i>
          Nuevo Miembro
        </button>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3><i className="fas fa-cog"></i> Configuración de Sección</h3>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <label><i className="fas fa-tag"></i> Subtítulo</label>
              <input
                type="text"
                value={teamData.subtitle || ''}
                onChange={(e) => handleSectionChange(e, 'subtitle')}
                placeholder="Ej: Nuestro Equipo"
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-heading"></i> Título</label>
              <input
                type="text"
                value={teamData.title || ''}
                onChange={(e) => handleSectionChange(e, 'title')}
                placeholder="Ej: Equipo Docente"
              />
            </div>
          </div>
          
          <div className="form-actions" style={{ marginTop: '15px' }}>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleSaveTitles}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="spinner-small"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Guardar Títulos
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3><i className="fas fa-users"></i> Miembros del Equipo ({teamData.teamMembers.length})</h3>
        </div>
        <div className="card-body">
          {teamData.teamMembers.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-users"></i>
              <p>No hay miembros del equipo</p>
              <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
                <i className="fas fa-user-plus"></i>
                Agregar Primer Miembro
              </button>
            </div>
          ) : (
            <div className="team-grid">
              {teamData.teamMembers.map((member, index) => (
                <div key={index} className="team-card">
                  <div className="team-card-drag">
                    <i className="fas fa-grip-vertical"></i>
                  </div>
                  
                  <div className="team-card-image">
                    <img 
                      src={member.image.startsWith('http') 
                        ? member.image 
                        : `${window.location.origin}${member.image}`
                      }  
                      alt={member.name} 
                    />
                    <div className="team-card-overlay">
                      <button type="button" className="btn-overlay" onClick={() => handleOpenModal(member, index)} disabled={saving}>
                        <i className="fas fa-edit"></i>
                      </button>
                    </div>
                  </div>
                  
                  <div className="team-card-content">
                    <h4>{member.name}</h4>
                    <p className="team-role">{member.role}</p>
                    
                    {member.socialLinks && (
                      <div className="team-social">
                        {member.socialLinks.facebook && (
                          <a href={member.socialLinks.facebook} target="_blank" rel="noopener noreferrer" title="Facebook">
                            <i className="fab fa-facebook"></i>
                          </a>
                        )}
                        {member.socialLinks.twitter && (
                          <a href={member.socialLinks.twitter} target="_blank" rel="noopener noreferrer" title="Twitter">
                            <i className="fab fa-twitter"></i>
                          </a>
                        )}
                        {member.socialLinks.youtube && (
                          <a href={member.socialLinks.youtube} target="_blank" rel="noopener noreferrer" title="YouTube">
                            <i className="fab fa-youtube"></i>
                          </a>
                        )}
                        {member.socialLinks.linkedin && (
                          <a href={member.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" title="LinkedIn">
                            <i className="fab fa-linkedin"></i>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="team-card-actions">
                    <button type="button" className="btn-icon" onClick={() => handleMoveMember(index, 'up')} disabled={index === 0 || saving} title="Subir">
                      <i className="fas fa-arrow-up"></i>
                    </button>
                    <button type="button" className="btn-icon" onClick={() => handleMoveMember(index, 'down')} disabled={index === teamData.teamMembers.length - 1 || saving} title="Bajar">
                      <i className="fas fa-arrow-down"></i>
                    </button>
                    <button type="button" className="btn-icon btn-delete" onClick={() => handleOpenDeleteModal(member, index)} disabled={saving} title="Eliminar">
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MODAL EDITAR/AGREGAR MIEMBRO */}
      {showModal && (
        <div className="team-modal-overlay">
          <div className="team-modal-container">
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-user"></i>
                {editingMember ? 'Editar Miembro' : 'Nuevo Miembro'}
              </h3>
              <button className="team-modal-close" onClick={handleCloseModal} type="button" disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              {validationError && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                  <i className="fas fa-exclamation-triangle"></i>
                  {validationError}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Nombre Completo *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Ej: María González" required disabled={saving} />
                </div>

                <div className="form-group">
                  <label>Cargo/Posición *</label>
                  <input type="text" name="role" value={formData.role} onChange={handleChange} placeholder="Ej: Directora" required disabled={saving} />
                </div>
              </div>

              <div className="form-group">
                <label>Fotografía *</label>
                
                {formData.image && (
                  <div className="photo-preview">
                    <img 
                      src={formData.image.startsWith('http') 
                        ? formData.image 
                        : `${window.location.origin}${formData.image}`
                      } 
                      alt="Preview" 
                    />
                  </div>
                )}
                
                <div className="image-upload-group">
                  <input type="text" name="image" value={formData.image} onChange={handleChange} placeholder="/assets/kidsa/assets/img/team/01.jpg" required disabled={saving} />
                  
                  <button type="button" className="btn btn-upload" disabled={uploadingImage || saving} onClick={() => document.getElementById('team-image-input')?.click()}>
                    {uploadingImage ? (
                      <><div className="spinner-small"></div> Subiendo...</>
                    ) : (
                      <><i className="fas fa-upload"></i> Subir Foto</>
                    )}
                  </button>
                  <input id="team-image-input" type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                </div>
                <small className="hint">PNG, JPG, WEBP (máx 3MB)</small>
              </div>

              <div className="form-group">
                <label>Team Shape (opcional)</label>
                <input type="text" name="teamShape" value={formData.teamShape || ''} onChange={handleChange} placeholder="/assets/kidsa/assets/img/team/team-shape-1.png" disabled={saving} />
              </div>

              <hr className="divider" />

              <h4 className="section-subtitle"><i className="fas fa-share-alt"></i> Redes Sociales</h4>
              <p className="hint">Opcional - Deja en blanco las redes que no uses</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fab fa-facebook"></i> Facebook</label>
                  <input type="url" name="social.facebook" value={formData.socialLinks?.facebook || ''} onChange={handleChange} placeholder="https://facebook.com/usuario" disabled={saving} />
                </div>

                <div className="form-group">
                  <label><i className="fab fa-twitter"></i> Twitter</label>
                  <input type="url" name="social.twitter" value={formData.socialLinks?.twitter || ''} onChange={handleChange} placeholder="https://twitter.com/usuario" disabled={saving} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><i className="fab fa-youtube"></i> YouTube</label>
                  <input type="url" name="social.youtube" value={formData.socialLinks?.youtube || ''} onChange={handleChange} placeholder="https://youtube.com/@usuario" disabled={saving} />
                </div>

                <div className="form-group">
                  <label><i className="fab fa-linkedin"></i> LinkedIn</label>
                  <input type="url" name="social.linkedin" value={formData.socialLinks?.linkedin || ''} onChange={handleChange} placeholder="https://linkedin.com/in/usuario" disabled={saving} />
                </div>
              </div>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveMember} disabled={saving}>
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    {editingMember ? 'Actualizar' : 'Agregar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMACIÓN ELIMINAR */}
      {showDeleteModal && memberToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button className="team-modal-close" onClick={handleCloseDeleteModal} type="button" disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Estás seguro que deseas eliminar a <strong>{memberToDelete.member.name}</strong> del equipo?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeam;